import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { Box, Button, Dialog, Grid, Tooltip, useTheme } from '@mui/material'
import { Add, FlagCircle, Print } from '@mui/icons-material'
import { Form, Formik, useField, useFormikContext } from 'formik'

import {
  useDELETE_rtiInterventionMutation,
  useGET_rtiInterventionByIdQuery,
  usePUT_rtiInterventionMutation,
  usePOST_rtiInterventionMutation,
  useGET_rtiErrorCodesSelectQuery,
} from 'redux/api/rtiApi'

import { 
  useGET_schoolSetupInterventionTypesSelectQuery,
  useGET_schoolSetupInterventionStrategiesSelectQuery,
  useGET_schoolSetupInstructionalStrategiesSelectQuery,
  useGET_schoolSetupAssessmentStrategiesSelectQuery,
  useGET_schoolSetupAccommodationsSelectQuery,
  useGET_schoolSetupModificationsSelectQuery,
  useGET_schoolSetupMonitoringMethodsSelectQuery,
  useGET_schoolSetupProgressMonitoringToolsSelectQuery,
  useGET_schoolSetupAssessmentTypesSelectQuery,
  useGET_modificationCategoriesSelectQuery,
  useGET_accommodationCategoriesSelectQuery,
} from 'redux/api/setupApi'

import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'
import { useGET_usersSelectQuery } from 'redux/api/usersApi'
import { useGET_rubricSelectQuery } from 'redux/api/rubricApi'

import { setCurriculumSelection, setGoalbankSelection } from 'redux/ui/goalbankUI'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'
import SelectField from 'components/atoms/fields/SelectField'
import CheckboxField from 'components/atoms/fields/CheckboxField'
import DialogPopup from 'components/molecules/DialogPopup'
import SectionTitle from 'components/atoms/SectionTitle'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import formModel, { initialValues } from 'utils/formModels/rtiInterventionModel'
import { 
  crudColorMap,
  crudIconMap,
  rtiTierSelectData,
  rtiInterventionStatusSelectData,
  frequencySelectData,
  rtiGroupSizeSelectData
}  from 'utils/helpers'
import useCrudData from 'utils/customHooks/useCrudData'


import GoalbankPage from 'pages/protected/GoalbankPage'
import { ActionType } from 'types'

import { AppDispatch, RootState } from 'redux/store'
import { useDispatch, useSelector } from 'react-redux'

import _ from 'lodash'
import RichTextField from 'components/atoms/fields/RichTextField'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import DateTimePickerField from 'components/atoms/fields/DateTimePickerField'
import { useNavigate } from 'react-router-dom'
import AccommodationList from 'components/utilized/common/AccommodationList'
import ModificationList from 'components/utilized/common/ModificationList'
import GoalbankSelection from 'components/utilized/goalbank/GoalbankSelection'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
  onSuccess?: any
}

const RtiInterventionForm: FC<IProps> = ({  action, id, viewOnly, onSuccess=null }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  
  const backToSlug = `/rti-interventions`  //TODO: if student_id -> /rti-students/{id}#criteria 
  const editSlug = `/rti-interventions/edit/${id}`

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.rti-interventions')}`,
    entityId: id,
    getEntityQuery: useGET_rtiInterventionByIdQuery,
    createEntityMutation: usePOST_rtiInterventionMutation,
    editEntityMutation: usePUT_rtiInterventionMutation,
    deleteEntityMutation: useDELETE_rtiInterventionMutation,
    onSuccess: () => {
      if(onSuccess){
        onSuccess()
      }
    }
  })

  const {
    student,
    intervention_type,
    title,
    status,
    intervention_provider,
    intervention_provider_position,
    evaluation_provider,
    rti_error_codes,
    intervention_strategies,
    instructional_strategies,
    baseline_assessment_strategies,
    accommodations,
    modifications,
    progress_monitoring_tools,
    monitoring_methods,
    assessment_types,
    progress_rubric,
    need_for_support,
    observed_difficulty,
    description,
    student_strengths,
    student_weaknesses,
    goal_benchmarks,
    goal_standards,
    goal_short_term,
    goal_long_term,
    goal_outcomes,
    custom_strategy,
    tier,
    start_date,
    end_date,
    monitoring_times_a_week,
    monitoring_start_datetime,
    monitoring_duration_minutes,
    group_size,
    has_accommodations,
    has_modifications,
    measure,
    weekly_scores,
    baseline,
    goal_score,
    goal_progress_rate,
    student_progress_rate,
    goal_achieved,
    observation,
    data_collection_time,
    tracking,
    teacher_meeting_times_a_month,
    parent_meeting_times_a_month,
    share_progress_with_teachers,
    share_progress_with_parents,
    notes,
    staff_collaboration_meeting_times_a_month,
    evaluation_start_datetime,
    evaluation_duration_minutes,
    evaluation_frequency,
    progress_review_frequency,
    is_rate_plan_successful,
    rate_plan_effectiveness,
    rate_plan_fidelity,
    rate_comments
  } = formModel()


  const serverValues = entity?.id ? {
    ...entity,

    accommodations:  _.uniqBy(
      entity.accommodations.map(a => {return {category_id: a.category_id, category_title: a.category_title}}) as any[],
      a => [a.category_id, a.category_title].join()
    ).map(a => { return {
      category: {
        id: a.category_id,
        title: a.category_title
      },
      items: entity.accommodations.filter(item => a.category_id === item.category_id)
    }}),

    modifications:  _.uniqBy(
      entity.modifications.map(m=> {return {category_id: m.category_id, category_title: m.category_title}}) as any[],
      m => [m.category_id, m.category_title].join()
    ).map(m => { return {
      category: {
        id: m.category_id,
        title: m.category_title
      },
      items: entity.modifications.filter(item => m.category_id === item.category_id)
    }}),

    tier: entity.tier ? {
      id: entity.tier,
      title: t('rti.tier')+' '+entity.tier
    } : null,

    group_size: entity.group_size ? {
      id: entity.group_size,
      title: t('rti-group-size.group-size-'+entity.group_size)
    } : null,

    status: entity.status ? {
      id: entity.status,
      title: t('rti-intervention-status.'+entity.status)
    } : null,


    evaluation_frequency: entity.evaluation_frequency ? {
      id: entity.evaluation_frequency,
      title: t('common.'+entity.evaluation_frequency)
    } : null,

    progress_review_frequency: entity.progress_review_frequency ? {
      id: entity.progress_review_frequency,
      title: t('common.'+entity.progress_review_frequency)
    } : null,

    monitoring_times_a_week: entity.monitoring_times_a_week === 4 ? {
      id: 'weekly',
      title: t('common.weekly')
    } : entity.monitoring_times_a_week === 1 ? {
      id: 'monthly',
      title: t('common.monthly')
    } : null,

    parent_meeting_times_a_month: entity.parent_meeting_times_a_month === 4 ? {
      id: 'weekly',
      title: t('common.weekly')
    } : entity.parent_meeting_times_a_month === 1 ? {
      id: 'monthly',
      title: t('common.monthly')
    } : null,

    teacher_meeting_times_a_month: entity.teacher_meeting_times_a_month === 4 ? {
      id: 'weekly',
      title: t('common.weekly')
    } : entity.teacher_meeting_times_a_month === 1 ? {
      id: 'monthly',
      title: t('common.monthly')
    } : null,

    staff_collaboration_meeting_times_a_month: entity.staff_collaboration_meeting_times_a_month === 4 ? {
      id: 'weekly',
      title: t('common.weekly')
    } : entity.staff_collaboration_meeting_times_a_month === 1 ? {
      id: 'monthly',
      title: t('common.monthly')
    } : null,

  } : {}


  const formValues = {
    ...initialValues,
    ...serverValues,
  }


  const [goalbankOpen, setGoalbankOpen] = useState(false)
  const goalbankSelection = useSelector((state: RootState) => state['goalbank'].goalbankSelection)
  const curriculumSelection = useSelector((state: RootState) => state['goalbank'].curriculumSelection)

  useEffect(() => {
    dispatch(setGoalbankSelection(entity?.goalbank_selection ?? {}))
    dispatch(setCurriculumSelection(entity?.curriculum_selection ?? {}))
  }, [entity])


  return ( 
        <Formik
          enableReinitialize
          validateOnChange={false}
          validateOnBlur={true}
          initialValues={formValues}
          onSubmit={(values) => {


            const data = {
              ...values,

              rti_error_codes_ids: values?.rti_error_codes?.map(item => item.id) ?? [],  
              intervention_strategies_ids: values?.intervention_strategies?.map(item => item.id) ?? [],
              instructional_strategies_ids: values?.instructional_strategies?.map(item => item.id) ?? [],
              baseline_assessment_strategies_ids: values?.baseline_assessment_strategies?.map(item => item.id) ?? [],
              accommodations_ids: values?.accommodations?.map(a => a.items.map(item => item.id)).flat() ?? [],
              modifications_ids: values?.modifications?.map(m => m.items.map(item => item.id)).flat() ?? [],
              progress_monitoring_tools_ids: values?.progress_monitoring_tools?.map(item => item.id) ?? [],
              monitoring_methods_ids: values?.monitoring_methods?.map(item => item.id) ?? [],
              assessment_types_ids: values?.assessment_types?.map(item => item.id) ?? [],

              student_id: values?.student?.id ?? '',
              intervention_type_id: values?.intervention_type?.id ?? '',
              intervention_provider_id: values?.intervention_provider?.id ?? '',
              evaluation_provider_id: values?.evaluation_provider?.id ?? '',
              progress_rubric_id : values?.progress_rubric?.id ?? '',
              status: values?.status?.id ?? '',
              tier: values?.tier?.id ?? '',
              group_size: values?.group_size?.id ?? '',
              evaluation_frequency: values?.evaluation_frequency?.id ?? '',
              progress_review_frequency: values?.progress_review_frequency?.id ?? '',

              monitoring_times_a_week: values?.monitoring_times_a_week?.id === 'weekly' ? 4 : 1,
              parent_meeting_times_a_month: values?.parent_meeting_times_a_month?.id === 'weekly' ? 4 : 1,
              teacher_meeting_times_a_month: values?.teacher_meeting_times_a_month?.id === 'weekly' ? 4 : 1,
              staff_collaboration_meeting_times_a_month	: values?.staff_collaboration_meeting_times_a_month?.id === 'weekly' ? 4 : 1,

              goalbank_selection: goalbankSelection,
              curriculum_selection: curriculumSelection

            }

            handleSubmit(action, id, data)
          }}
        >
          {({ values, setFieldValue, errors }) => {
            return (
              <Form>

                <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>


                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.intervention-plan')}`} />
                  </Grid>   


                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                      fieldProps={{
                        ...student,
                      }}
                      apiCall={useGET_studentsProfileSelectQuery}
                      disabled={viewOnly}
                    />
                  </Grid>


                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                      fieldProps={{
                        ...intervention_provider,
                      }}
                      apiCall={useGET_usersSelectQuery}
                      disabled={viewOnly}
                    />
                  </Grid>


                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...intervention_provider_position }} disabled={viewOnly} />
                  </Grid>


                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                      fieldProps={{
                        ...intervention_type,
                      }}
                      apiCall={useGET_schoolSetupInterventionTypesSelectQuery}
                      disabled={viewOnly}
                      hasHint={true}
                    />
                  </Grid>


                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                   <SelectField
                      fieldProps={{
                        ...tier,
                      }}
                      apiCall={rtiTierSelectData}
                      disabled={viewOnly}
                    />
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...title }} disabled={viewOnly} />
                  </Grid>



                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.reason-for-intervention')}`} />
                  </Grid>  

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...rti_error_codes,
                        }}
                        multiple={true}
                        apiCall={useGET_rtiErrorCodesSelectQuery}
                        disabled={viewOnly}
                        hasHint={true}
                      />
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...need_for_support }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...observed_difficulty }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important' }}>
                    <RichTextField
                      fieldProps={{ ...description }}
                      label={description.label}
                      disabled={viewOnly}
                     />
                  </Grid>
 

                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.student-strengths-and-weaknesses')}`} />
                  </Grid>   

                  <Grid item xs={12} md={6} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...student_strengths }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...student_weaknesses }} disabled={viewOnly} />
                  </Grid>
                </Grid>


                      
                    <div style={{ position: 'relative', marginTop: '20px' }}>
                        <Button
                          onClick={() => setGoalbankOpen(true)}
                          aria-label={`${t('goalbank.use-curriculum-library')}`}
                          size='small'
                          disabled={viewOnly}
                          color='success'
                          variant='contained'
                          sx={{ boxShadow: 'none',  minHeight: '47px', }}
                          >
                            <FlagCircle style={{ fontSize: '1.5rem', marginRight: '10px' }} /> {t('goalbank.use-curriculum-library')}
                        </Button>

                        <div style={{ marginTop: '10px'}}>
                          <GoalbankSelection goalbankSelection={goalbankSelection} curriculumSelection={curriculumSelection}/>
                        </div>
                        
                    </div>

                <Grid container spacing={4}>      
                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.goals')}`} />
                  </Grid>   


                  <Grid item xs={12} md={6} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...goal_short_term }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...goal_long_term }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...goal_outcomes }} disabled={viewOnly} />
                  </Grid>



                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.strategies')}`} />
                  </Grid>   
        
                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...intervention_strategies,
                        }}
                        multiple={true}
                        apiCall={useGET_schoolSetupInterventionStrategiesSelectQuery}
                        disabled={viewOnly}
                        hasHint={true}
                      />
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...instructional_strategies,
                        }}
                        multiple={true}
                        apiCall={useGET_schoolSetupInstructionalStrategiesSelectQuery}
                        disabled={viewOnly}
                        hasHint={true}
                      />
                  </Grid>
          
                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...baseline_assessment_strategies,
                        }}
                        multiple={true}
                        apiCall={useGET_schoolSetupAssessmentStrategiesSelectQuery}
                        disabled={viewOnly}
                        hasHint={true}
                      />
                  </Grid>


                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...custom_strategy }} disabled={viewOnly} />
                  </Grid>



                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...measure }} disabled={viewOnly} />
                  </Grid>
                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...weekly_scores }} disabled={viewOnly} />
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...baseline }} disabled={viewOnly} />
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...goal_score }} disabled={viewOnly} />
                  </Grid>
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...goal_progress_rate }} disabled={viewOnly} />
                  </Grid>
                  <Grid item xs={12} sx={{ paddingTop: '20px !important', fontStyle: 'italic' }}>
                    <div>* From students from cultural and/or linguistic minority populations, consider whether to compare this student’s performance with that of “true” peers who are from the same language and/or cultural background.</div>
                    <div>** If applicable for assessment type (i.e., curriculum-based measures).</div>
                  </Grid>


                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.schedule-intervention')}`} />
                  </Grid>   
        
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <DatePickerField fieldProps={{ ...start_date }} disabled={viewOnly} />
                  </Grid>     
                     
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <DatePickerField fieldProps={{ ...end_date }} disabled={viewOnly} />
                  </Grid>        

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                        fieldProps={{
                          ...monitoring_times_a_week,
                        }}
                        apiCall={frequencySelectData}
                        disabled={viewOnly}
                      />
                  </Grid>        

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <DateTimePickerField fieldProps={{ ...monitoring_start_datetime  }} disabled={viewOnly} />
                  </Grid>     

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...monitoring_duration_minutes }} disabled={viewOnly} />
                  </Grid>
                
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                        fieldProps={{
                          ...group_size,
                        }}
                        apiCall={rtiGroupSizeSelectData}
                        disabled={viewOnly}
                      />
                  </Grid>   

                    
                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <CheckboxField fieldProps={{ ...has_accommodations }} disabled={viewOnly} />
                  </Grid>
          
                  <Grid item xs={12} md={9} sx={{ paddingTop: '20px !important', paddingBottom: '20px !important' }}> 
                    {values?.has_accommodations && 
                        <AccommodationList fieldProps={{ ...accommodations }}/>
                    }
                  </Grid>       

                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <CheckboxField fieldProps={{ ...has_modifications }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={9} sx={{ paddingTop: '20px !important', paddingBottom: '20px !important' }}> 
                    {values?.has_modifications && 
                        <ModificationList fieldProps={{ ...modifications }}/>
                    }
                  </Grid>       


                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.progress-monitoring')}`} />
                  </Grid>   

                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...monitoring_methods,
                        }}
                        multiple={true}
                        filters={{is_enabled: true}}
                        apiCall={useGET_schoolSetupMonitoringMethodsSelectQuery}
                        disabled={viewOnly}
                        hasHint={true}
                      />
                  </Grid>

                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...progress_monitoring_tools,
                        }}
                        multiple={true}
                        filters={{is_enabled: true }}
                        apiCall={useGET_schoolSetupProgressMonitoringToolsSelectQuery}
                        disabled={viewOnly}
                        hasHint={true}
                      />
                  </Grid>

                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...assessment_types,
                        }}
                        multiple={true}
                        apiCall={useGET_schoolSetupAssessmentTypesSelectQuery}
                        disabled={viewOnly}
                      />
                  </Grid>


                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...progress_rubric,
                        }}
                        apiCall={useGET_rubricSelectQuery}
                        disabled={viewOnly}
                      />
                  </Grid>

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <CheckboxField fieldProps={{ ...goal_achieved }} disabled={viewOnly} />
                  </Grid>
                        
                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...student_progress_rate }} disabled={viewOnly} />
                  </Grid>
                
                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...data_collection_time }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...tracking }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important' }}>
                    <RichTextField
                      fieldProps={{ ...observation }}
                      label={observation.label}
                      disabled={viewOnly}
                     />
                  </Grid>



                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.collaboration-and-communication')}`} />
                  </Grid>   

                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <CheckboxField fieldProps={{ ...share_progress_with_teachers }} disabled={viewOnly} />
                  </Grid>

                  <Grid item xs={12} md={3} sx={{ paddingTop: '20px !important' }}>
                    <CheckboxField fieldProps={{ ...share_progress_with_parents }} disabled={viewOnly} />
                  </Grid>


                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                        fieldProps={{
                          ...teacher_meeting_times_a_month,
                        }}
                        apiCall={frequencySelectData}
                        disabled={viewOnly}
                      />
                  </Grid>    

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                        fieldProps={{
                          ...parent_meeting_times_a_month,
                        }}
                        apiCall={frequencySelectData}
                        disabled={viewOnly}
                      />
                  </Grid>    

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                        fieldProps={{
                          ...staff_collaboration_meeting_times_a_month,
                        }}
                        apiCall={frequencySelectData}
                        disabled={viewOnly}
                      />
                  </Grid>    



                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important' }}>
                    <RichTextField
                      fieldProps={{ ...notes }}
                      label={notes.label}
                      disabled={viewOnly}
                     />
                  </Grid>




                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.evaluation')}`} />
                  </Grid>   

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                        fieldProps={{
                          ...evaluation_provider,
                        }}
                        apiCall={useGET_usersSelectQuery}
                        disabled={viewOnly}
                      />
                  </Grid>

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <DateTimePickerField fieldProps={{ ...evaluation_start_datetime  }} disabled={viewOnly} />
                  </Grid>     

                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...evaluation_duration_minutes }} disabled={viewOnly} />
                  </Grid>
                  
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                      <SelectField
                          fieldProps={{
                            ...evaluation_frequency,
                          }}
                          apiCall={frequencySelectData}
                          disabled={viewOnly}
                        />
                  </Grid>        
                  
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                        fieldProps={{
                            ...progress_review_frequency,
                        }}
                        apiCall={frequencySelectData}
                        disabled={viewOnly}
                      />
                  </Grid>   





                  <Grid item xs={12} md={12}>
                    <SectionTitle text={`${t('rti.program-status')}`} />
                  </Grid>   

                  <Grid item xs={12} lg={6} sx={{ paddingTop: '20px !important' }}>
                    <SelectField
                        fieldProps={{
                          ...status,
                        }}
                        apiCall={rtiInterventionStatusSelectData}
                        disabled={viewOnly}
                      />
                  </Grid>



                  <Grid item xs={12} md={12}>
                      <SectionTitle text={`${t('rti.rating')}`} />
                  </Grid>   

                  <Grid item xs={12} md={4} sx={{ paddingTop: '20px !important' }}>
                    <CheckboxField fieldProps={{ ...is_rate_plan_successful }} disabled={viewOnly} />
                  </Grid>
        
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...rate_plan_effectiveness }} disabled={viewOnly} />
                  </Grid>
        
                  <Grid item xs={12} md={2} sx={{ paddingTop: '20px !important' }}>
                    <InputField fieldProps={{ ...rate_plan_fidelity }} disabled={viewOnly} />
                  </Grid>
        
                  <Grid item xs={12} md={12} sx={{ paddingTop: '20px !important' }}>
                    <RichTextField
                        fieldProps={{ ...rate_comments }}
                        label={rate_comments.label}
                        disabled={viewOnly}
                      />
                  </Grid>




                </Grid>


                       




                {/* ACTIONS */}
                <Box sx={{ marginTop: '20px' }}>

                  {/*
                  <Button
                        variant='outlined'
                        color='black'
                        onClick={ () => { printDiv('lesson-plan') } }
                        sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                      >
                        <Print style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                        {`${t('common.print')}`}
                  </Button>
                    */}

                  {!onSuccess && <Box sx={{ display: 'inline-block' }}>   
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(backToSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('common.back')}`}
                    </Button>
                  </Box> }

                  <Box sx={{ display: 'inline-block', marginLeft: '10px' }}>   
                    <SubmitButton
                              color={crudColorMap[action]}
                              text={`${t('form-actions.' + action)}`}
                              icon={crudIconMap[action]}
                              sx={{ minHeight: '2.5rem', width: '10rem' }}
                              isLoading={isLoading}
                          />
                  </Box>   
                </Box>


                <DialogPopup
                  title={t('common.goalbank')}
                  open={goalbankOpen}
                  onClose={() => setGoalbankOpen(false)}
                  popupWidth={'1400px'}
                >
                  <GoalbankPage hasSelection={true}/>
                </DialogPopup>
        
              </Form>
            )
          }}
        </Formik>
  )
}
export default RtiInterventionForm
